import { useState, useEffect } from 'react';
import { Brain, Sparkles, Crown, Target, Shield, TrendingUp, Scissors, Droplets, Rocket, Radar, Loader2, MessageSquare, Send, Briefcase, Award, Zap, BarChart3, LineChart, Activity, Calculator, ChevronRight, AlertTriangle, CheckCircle, TrendingDown, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { LineChart as ReLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import axios from 'axios';
import { useApp } from '../context/AppContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AI_ANALYSIS_TYPES = [
  { id: 'ceo_briefing', label: 'CEO Briefing', icon: Crown, color: 'from-amber-500 to-yellow-600', description: 'Executive summary untuk board meeting', tagline: 'Apa yang CEO perlu tahu SEKARANG', category: 'strategic' },
  { id: 'profit_maximizer', label: 'Profit Maximizer', icon: TrendingUp, color: 'from-emerald-500 to-green-600', description: 'Strategi maximize profit dalam 90 hari', tagline: 'Squeeze setiap sen profit', category: 'strategic' },
  { id: 'cost_killer', label: 'Cost Killer', icon: Scissors, color: 'from-red-500 to-rose-600', description: 'Potong kos tanpa sacrifice growth', tagline: 'Cut fat, keep muscle', category: 'strategic' },
  { id: 'cashflow_doctor', label: 'Cashflow Doctor', icon: Droplets, color: 'from-blue-500 to-cyan-600', description: 'Diagnosis dan rawatan cash flow', tagline: 'Cash is oxygen', category: 'strategic' },
  { id: 'growth_strategist', label: 'Growth Strategist', icon: Rocket, color: 'from-purple-500 to-violet-600', description: 'Scale business 2-3x dalam 12-24 bulan', tagline: 'Think bigger, scale faster', category: 'strategic' },
  { id: 'risk_radar', label: 'Risk Radar', icon: Radar, color: 'from-orange-500 to-amber-600', description: 'Identify semua potential risks', tagline: 'Hope for best, plan for worst', category: 'strategic' },
  // NEW Analysis Types
  { id: 'trend_pattern', label: 'Trend & Pattern', icon: LineChart, color: 'from-indigo-500 to-blue-600', description: 'Analisis trend 6 bulan & pattern detection', tagline: 'See the patterns others miss', category: 'analytics' },
  { id: 'predictive', label: 'Predictive Analytics', icon: Activity, color: 'from-pink-500 to-rose-600', description: 'Ramalan 3 bulan akan datang', tagline: 'Predict the future', category: 'analytics' },
  { id: 'benchmark', label: 'Industry Benchmark', icon: BarChart3, color: 'from-teal-500 to-emerald-600', description: 'Compare dengan industry standards', tagline: 'Where do you stand?', category: 'analytics' },
  { id: 'scenario', label: 'Scenario Planner', icon: Calculator, color: 'from-violet-500 to-purple-600', description: 'What-if analysis & simulations', tagline: 'Plan for every possibility', category: 'analytics' }
];

const formatCurrency = (value) => `RM${(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Insights Dashboard Component
const InsightsDashboard = ({ insights, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl p-6 border border-gray-100 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="grid grid-cols-3 gap-4">
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!insights) return null;

  const { trends, predictions, benchmarks, patterns } = insights;

  const getTrendIcon = (direction) => {
    if (direction?.includes('up')) return <ArrowUpRight className="w-4 h-4 text-emerald-500" />;
    if (direction?.includes('down')) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getBenchmarkColor = (rating) => {
    if (rating === 'excellent') return 'text-emerald-600 bg-emerald-50';
    if (rating === 'good') return 'text-blue-600 bg-blue-50';
    if (rating === 'average') return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="space-y-4">
      {/* Patterns Alert */}
      {patterns?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" /> Patterns Detected
          </h4>
          <div className="space-y-1">
            {patterns.map((p, idx) => (
              <div key={idx} className={`text-sm px-2 py-1 rounded ${
                p.severity === 'critical' ? 'bg-red-100 text-red-700' :
                p.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                'bg-emerald-100 text-emerald-700'
              }`}>
                {p.description}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Revenue Trend */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Revenue Trend</span>
            {getTrendIcon(trends?.revenue?.trend?.direction)}
          </div>
          <p className="text-lg font-bold text-gray-800">
            {trends?.revenue?.trend?.avg_change > 0 ? '+' : ''}{trends?.revenue?.trend?.avg_change || 0}%
          </p>
          <p className="text-xs text-gray-400">per month avg</p>
        </div>

        {/* Expense Trend */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Expense Trend</span>
            {getTrendIcon(trends?.expenses?.trend?.direction)}
          </div>
          <p className="text-lg font-bold text-gray-800">
            {trends?.expenses?.trend?.avg_change > 0 ? '+' : ''}{trends?.expenses?.trend?.avg_change || 0}%
          </p>
          <p className="text-xs text-gray-400">per month avg</p>
        </div>

        {/* Profit Margin Benchmark */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Profit Margin</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${getBenchmarkColor(benchmarks?.profit_margin?.rating)}`}>
              {benchmarks?.profit_margin?.rating?.toUpperCase()}
            </span>
          </div>
          <p className="text-lg font-bold text-gray-800">
            Top {100 - (benchmarks?.profit_margin?.percentile || 50)}%
          </p>
          <p className="text-xs text-gray-400">vs industry</p>
        </div>

        {/* Next Month Prediction */}
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-gray-500">Next Month Revenue</span>
            <Activity className="w-4 h-4 text-purple-500" />
          </div>
          <p className="text-lg font-bold text-gray-800">
            {formatCurrency(predictions?.revenue?.[0]?.predicted)}
          </p>
          <p className="text-xs text-gray-400">{predictions?.revenue?.[0]?.confidence || 0}% confidence</p>
        </div>
      </div>

      {/* Mini Trend Chart */}
      {trends?.revenue?.monthly_data?.length > 0 && (
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <h4 className="text-sm font-medium text-gray-700 mb-3">6-Month Revenue Trend</h4>
          <ResponsiveContainer width="100%" height={120}>
            <AreaChart data={trends.revenue.monthly_data}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `RM${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Area type="monotone" dataKey="amount" stroke="#10B981" fill="url(#colorRevenue)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// Scenario Planner Component
const ScenarioPlanner = ({ onRunScenario, loading }) => {
  const [scenarioType, setScenarioType] = useState('revenue_change');
  const [changePercent, setChangePercent] = useState(0);
  const [newStaffCount, setNewStaffCount] = useState(1);
  const [newStaffSalary, setNewStaffSalary] = useState(3000);
  const [customDescription, setCustomDescription] = useState('');

  const handleSubmit = () => {
    onRunScenario({
      scenario_type: scenarioType,
      change_percent: changePercent,
      new_staff_count: newStaffCount,
      new_staff_salary: newStaffSalary,
      custom_description: customDescription
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-4">
        <Calculator className="w-5 h-5 text-purple-600" />
        Scenario Planner
      </h3>

      <div className="space-y-4">
        {/* Scenario Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Jenis Scenario</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'revenue_change', label: 'Revenue +/-', icon: TrendingUp },
              { id: 'expense_change', label: 'Expense +/-', icon: TrendingDown },
              { id: 'staff_change', label: 'Hire Staff', icon: Briefcase },
              { id: 'custom', label: 'Custom', icon: MessageSquare }
            ].map(type => (
              <button
                key={type.id}
                onClick={() => setScenarioType(type.id)}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  scenarioType === type.id 
                    ? 'border-purple-500 bg-purple-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <type.icon className={`w-4 h-4 mb-1 ${scenarioType === type.id ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className="text-sm font-medium">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Scenario Parameters */}
        {scenarioType === 'revenue_change' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Revenue Change: <span className={changePercent >= 0 ? 'text-emerald-600' : 'text-red-600'}>{changePercent > 0 ? '+' : ''}{changePercent}%</span>
            </label>
            <input
              type="range"
              min="-50"
              max="100"
              value={changePercent}
              onChange={(e) => setChangePercent(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>-50%</span>
              <span>0%</span>
              <span>+100%</span>
            </div>
          </div>
        )}

        {scenarioType === 'expense_change' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expense Change: <span className={changePercent <= 0 ? 'text-emerald-600' : 'text-red-600'}>{changePercent > 0 ? '+' : ''}{changePercent}%</span>
            </label>
            <input
              type="range"
              min="-30"
              max="50"
              value={changePercent}
              onChange={(e) => setChangePercent(parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        )}

        {scenarioType === 'staff_change' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bilangan Staff Baru</label>
              <input
                type="number"
                min="1"
                max="10"
                value={newStaffCount}
                onChange={(e) => setNewStaffCount(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gaji (RM)</label>
              <input
                type="number"
                min="1500"
                max="20000"
                step="500"
                value={newStaffSalary}
                onChange={(e) => setNewStaffSalary(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        )}

        {scenarioType === 'custom' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Describe Your Scenario</label>
            <textarea
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder="cth: Kalau buka cawangan baru di Johor dengan modal RM100k..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24"
            />
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading || (scenarioType === 'custom' && !customDescription.trim())}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-violet-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
          ) : (
            <><Calculator className="w-4 h-4" /> Run Scenario Analysis</>
          )}
        </button>
      </div>
    </div>
  );
};

// Analysis Result Component
const AnalysisResult = ({ result, loading, analysisType }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative">
          <Brain className="w-16 h-16 text-red-700 animate-pulse" />
          <Sparkles className="w-6 h-6 text-amber-500 absolute -top-1 -right-1 animate-bounce" />
        </div>
        <p className="text-gray-600 mt-4 font-medium">AI Consultant sedang menganalisis...</p>
        <p className="text-gray-400 text-sm mt-1">Membaca data, mengira metrik, menyusun strategi...</p>
      </div>
    );
  }

  if (!result) return null;

  const typeConfig = AI_ANALYSIS_TYPES.find(t => t.id === analysisType);

  return (
    <div className="space-y-4">
      <div className={`bg-gradient-to-r ${typeConfig?.color || 'from-red-500 to-rose-600'} rounded-xl p-4 text-white`}>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
            {typeConfig?.icon && <typeConfig.icon className="w-6 h-6" />}
          </div>
          <div>
            <h3 className="font-bold text-lg">{typeConfig?.label || 'Analysis'}</h3>
            <p className="text-white/80 text-sm">{typeConfig?.tagline}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="prose prose-sm max-w-none">
          <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
            {result.analysis?.split('\n').map((line, idx) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                return (
                  <h4 key={idx} className="font-bold text-gray-900 mt-4 mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    {line.replace(/\*\*/g, '')}
                  </h4>
                );
              }
              if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                return <p key={idx} className="ml-4 my-1 text-gray-600">{line}</p>;
              }
              if (/^\d+\./.test(line.trim())) {
                return <p key={idx} className="font-medium text-gray-800 mt-3 mb-1">{line}</p>;
              }
              if (line.includes('WARNING') || line.includes('CRITICAL') || line.includes('URGENT')) {
                return (
                  <p key={idx} className="text-red-600 font-semibold my-2 bg-red-50 p-2 rounded border-l-4 border-red-500">
                    {line}
                  </p>
                );
              }
              return line ? <p key={idx} className="my-1">{line}</p> : <br key={idx} />;
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <span className="flex items-center gap-1">
          <Award className="w-3 h-3" />
          30 Years Business Experience
        </span>
        <span>{new Date(result.timestamp).toLocaleString('ms-MY')}</span>
      </div>
    </div>
  );
};

// Main AI Analysis Page
const AIAnalysisPage = () => {
  const { loading: appLoading } = useApp();
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [scenarioResult, setScenarioResult] = useState(null);
  const [activeTab, setActiveTab] = useState('strategic');

  // Fetch insights on load
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const response = await axios.get(`${API}/ai/insights`);
        setInsights(response.data);
      } catch (error) {
        console.error('Failed to fetch insights:', error);
      } finally {
        setInsightsLoading(false);
      }
    };
    fetchInsights();
  }, []);

  const handleAnalysis = async (analysisType) => {
    setAiLoading(true);
    setAiResult(null);
    setScenarioResult(null);
    setSelectedType(analysisType);
    
    try {
      const response = await axios.post(`${API}/ai/analyze`, {
        analysis_type: analysisType,
        prompt: ''
      });
      setAiResult(response.data);
    } catch (error) {
      console.error('AI Analysis error:', error);
      setAiResult({
        analysis: 'Maaf, analisis AI tidak dapat dijalankan sekarang. Sila pastikan ada data dalam sistem dan cuba lagi.',
        analysis_type: analysisType,
        timestamp: new Date().toISOString()
      });
    }
    
    setAiLoading(false);
  };

  const handleScenario = async (scenarioData) => {
    setAiLoading(true);
    setAiResult(null);
    setScenarioResult(null);
    setSelectedType('scenario');
    
    try {
      const response = await axios.post(`${API}/ai/scenario`, scenarioData);
      setScenarioResult(response.data);
    } catch (error) {
      console.error('Scenario error:', error);
    }
    
    setAiLoading(false);
  };

  if (appLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  const strategicTypes = AI_ANALYSIS_TYPES.filter(t => t.category === 'strategic');
  const analyticsTypes = AI_ANALYSIS_TYPES.filter(t => t.category === 'analytics');

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="bg-gradient-to-r from-gray-900 via-red-900 to-gray-900 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAyNHYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
        <div className="relative">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              <Brain className="w-10 h-10 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                AI Business Consultant
                <Crown className="w-6 h-6 text-amber-400" />
              </h1>
              <p className="text-gray-300 text-sm mt-1">
                Trend Analysis • Predictive Analytics • Industry Benchmarking • Scenario Planning
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Insights Dashboard */}
      <InsightsDashboard insights={insights} loading={insightsLoading} />

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('strategic')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'strategic' 
              ? 'border-red-700 text-red-700' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Briefcase className="w-4 h-4 inline mr-2" />
          Strategic Analysis
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'analytics' 
              ? 'border-red-700 text-red-700' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <LineChart className="w-4 h-4 inline mr-2" />
          Advanced Analytics
        </button>
      </div>

      {/* Analysis Type Grid */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
        <h3 className="text-gray-800 font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-500" /> 
          {activeTab === 'strategic' ? 'Pilih Jenis Analisis Strategik' : 'Pilih Jenis Analytics'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(activeTab === 'strategic' ? strategicTypes : analyticsTypes.filter(t => t.id !== 'scenario')).map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => handleAnalysis(type.id)}
                disabled={aiLoading}
                className={`group relative p-5 rounded-xl border-2 transition-all text-left overflow-hidden ${
                  selectedType === type.id 
                    ? 'border-red-700 bg-red-50 shadow-lg' 
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                } ${aiLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${type.color} opacity-0 group-hover:opacity-5 transition-opacity`}></div>
                <div className="relative">
                  <div className={`inline-flex p-2.5 rounded-xl bg-gradient-to-br ${type.color} text-white mb-3`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-semibold text-gray-800">{type.label}</h4>
                  <p className="text-gray-500 text-xs mt-1">{type.description}</p>
                  <p className="text-gray-400 text-xs mt-2 italic">&quot;{type.tagline}&quot;</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Scenario Planner - Only show in Analytics tab */}
      {activeTab === 'analytics' && (
        <ScenarioPlanner onRunScenario={handleScenario} loading={aiLoading && selectedType === 'scenario'} />
      )}

      {/* Analysis Result */}
      {(aiResult || aiLoading) && !scenarioResult && (
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
          <AnalysisResult result={aiResult} loading={aiLoading} analysisType={selectedType} />
        </div>
      )}

      {/* Scenario Result */}
      {scenarioResult && (
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-100 space-y-4">
          {/* Scenario Data Summary */}
          {scenarioResult.scenario_data?.scenario && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <h4 className="font-semibold text-purple-800 mb-3">Scenario: {scenarioResult.scenario_data.scenario.type}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {Object.entries(scenarioResult.scenario_data.scenario)
                  .filter(([key]) => key !== 'type' && key !== 'description')
                  .map(([key, value]) => (
                    <div key={key} className="bg-white rounded-lg p-3">
                      <p className="text-gray-500 text-xs capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="font-semibold text-gray-800">
                        {typeof value === 'number' ? (key.includes('ratio') || key.includes('change') ? `${value}%` : formatCurrency(value)) : value}
                      </p>
                    </div>
                  ))}
              </div>
            </div>
          )}
          
          {/* AI Analysis */}
          <AnalysisResult 
            result={{ analysis: scenarioResult.analysis, timestamp: scenarioResult.timestamp }} 
            loading={false} 
            analysisType="scenario" 
          />
        </div>
      )}
    </div>
  );
};

export default AIAnalysisPage;
